/**
 * iter146 — `/track/split/:token` no longer renders its own page.
 *
 * The Split-Payment view has been merged into the single unified
 * `/track/:token` tracker (see ClientTracker.jsx). To preserve
 * backward compatibility with existing WhatsApp links that were
 * shared before iter146, this wrapper:
 *
 *   1. Fetches `/api/public/track/split/{token}` (still supported
 *      server-side) to resolve the associated main tracker token.
 *   2. Redirects to `/track/{main_tracker_token}#payment-split` so
 *      the client lands on the same page as the couple, anchored
 *      at the Split Payment section.
 *   3. Falls back to a friendly error message when the split token
 *      is no longer valid (booking archived, tokens rotated, etc.).
 */
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Loader2, AlertCircle } from "lucide-react";

export default function SplitTracker() {
  const { token } = useParams();
  const [target, setTarget] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/track/split/${token}`);
        if (cancelled) return;
        const main = data?.main_tracker_token;
        if (main) {
          setTarget(`/track/${main}#payment-split`);
        } else {
          // No main tracker on file — surface a friendly message rather
          // than a broken redirect loop.
          setErr("This split-payment link is not yet linked to a project tracker. Please ask the studio to reshare a fresh link.");
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.response?.data?.detail || "This split-payment link is no longer valid.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (target) {
    return <Navigate to={target} replace />;
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fbf5ea] px-6" data-testid="split-tracker-redirect-error">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-rose-600 mx-auto mb-3" />
          <div className="text-lg font-semibold text-[#3b2f24] mb-2">Payment link unavailable</div>
          <div className="text-sm text-stone-600">{err}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fbf5ea]" data-testid="split-tracker-redirect-loading">
      <Loader2 className="w-6 h-6 animate-spin text-[#b18860]" />
    </div>
  );
}
